import { c as _c } from "react/compiler-runtime";
import { darkTheme, defaultTheme } from "@rigid/viewer";
import { createLayoutEngine, type LayoutEngine, type LayoutResultNode } from "@rigid/viewer/layout";
import { GraphCanvas, type GraphCanvasHandle, MiniMap, NodeOverlay } from "@rigid/viewer/react";
import { useEffect, useRef, useState } from "react";
import { layoutInput, shapeOf, toViewerGraph, topologyKey } from "@/lib/graph";
import { useDark } from "@/lib/hooks";
import { api } from "@/api";
import { useLoad } from "@/lib/load";
import { ErrorLine } from "@/components/error-line";
const EMPTY = {
  nodes: [],
  edges: []
};
const THEME = {
  light: {
    ...defaultTheme,
    background: "#ffffff",
    grid: "#f2f2f2",
    selection: "#0a0a0a",
    minimapBackground: "#fafafa",
    minimapViewport: "rgba(0, 0, 0, 0.04)",
    minimapViewportStroke: "rgba(0, 0, 0, 0.35)"
  },
  dark: {
    ...darkTheme,
    background: "#0a0a0a",
    grid: "#161616",
    selection: "#ededed",
    minimapBackground: "#111111",
    minimapViewport: "rgba(255, 255, 255, 0.05)",
    minimapViewportStroke: "rgba(255, 255, 255, 0.35)"
  }
};

/** Graph page: the canvas owns pointer and touch input; selecting a node opens its detail. */
export function GraphView(t0) {
  const $ = _c(40);
  const {
    selected,
    onOpen,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      kinds: ["*"]
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const loaded = useLoad(_temp, "graph", t1);
  const snapshot = loaded.data;
  const error = loaded.error;
  const dark = useDark();
  const [positions, setPositions] = useState(null);
  const [layoutError, setLayoutError] = useState(null);
  const canvas = useRef(null);
  const engine = useRef(null);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      engine.current = createLayoutEngine();
      return () => {
        engine.current?.dispose();
        engine.current = null;
      };
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let shape;
  let t4;
  if ($[3] !== snapshot) {
    shape = snapshot ? shapeOf(snapshot) : null;
    t4 = shape ? topologyKey(shape) : null;
    $[3] = snapshot;
    $[4] = shape;
    $[5] = t4;
  } else {
    shape = $[4];
    t4 = $[5];
  }
  const key = t4;
  let t5;
  if ($[6] !== shape) {
    t5 = () => {
      if (!shape || !engine.current) {
        return;
      }
      const abort = new AbortController();
      engine.current.layout({
        ...layoutInput(shape),
        direction: "RIGHT",
        algorithm: "stress",
        signal: abort.signal
      }).then(r => {
        setPositions(r.nodes);
        setLayoutError(null);
      }, e => abort.signal.aborted || setLayoutError(String(e?.message ?? e)));
      return () => abort.abort();
    };
    $[6] = shape;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== key) {
    t6 = [key];
    $[8] = key;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  useEffect(t5, t6);
  const fitted = useRef(false);
  let t7;
  if ($[10] !== positions?.length) {
    t7 = () => {
      if (positions?.length && !fitted.current) {
        fitted.current = true;
        requestAnimationFrame(() => void canvas.current?.fitView(false));
      }
    };
    $[10] = positions?.length;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== positions) {
    t8 = [positions];
    $[12] = positions;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t9;
  if ($[14] !== dark || $[15] !== positions || $[16] !== shape) {
    t9 = shape && positions ? toViewerGraph(shape, positions, dark) : EMPTY;
    $[14] = dark;
    $[15] = positions;
    $[16] = shape;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const graph = t9;
  const theme = THEME[dark ? "dark" : "light"];
  let t10;
  if ($[18] !== onClose || $[19] !== onOpen) {
    t10 = id => id ? onOpen(id) : onClose();
    $[18] = onClose;
    $[19] = onOpen;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== onOpen) {
    t11 = <NodeOverlay interactive={true} maxNodes={300} aria-label="Nodes">{node => <button type="button" data-node={node.id} aria-label={node.name} className="block h-full w-full rounded-field opacity-0 focus-visible:opacity-100" onClick={() => onOpen(node.id)} />}</NodeOverlay>;
    $[21] = onOpen;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== selected) {
    t12 = selected ? [selected] : null;
    $[23] = selected;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      position: "absolute",
      right: 12,
      top: "calc(12px + env(safe-area-inset-top))",
      width: 108,
      height: 72
    };
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t12 || $[27] !== theme) {
    t14 = <MiniMap aria-label="Minimap" theme={theme} highlightedIds={t12} className="rounded-box border border-base-300" style={t13} />;
    $[26] = t12;
    $[27] = theme;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== error || $[30] !== layoutError) {
    t15 = (error || layoutError) && <ErrorLine className="absolute top-2 left-3">{error ?? layoutError}</ErrorLine>;
    $[29] = error;
    $[30] = layoutError;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== graph || $[33] !== selected || $[34] !== t10 || $[35] !== t11 || $[36] !== t14 || $[37] !== t15 || $[38] !== theme) {
    t16 = <GraphCanvas ref={canvas} graph={graph} selectedId={selected} onSelectionChange={t10} theme={theme} aria-label="Graph" data-nodes={graph.nodes.length} data-edges={graph.edges.length} className="relative h-full w-full">{t11}{t14}{t15}</GraphCanvas>;
    $[32] = graph;
    $[33] = selected;
    $[34] = t10;
    $[35] = t11;
    $[36] = t14;
    $[37] = t15;
    $[38] = theme;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
}
function _temp() {
  return api.graph.load();
}